import { normalizeDialCode } from "./Helpers";

const norm = (v) => String(v == null ? "" : v).replace(/\s+/g, "").trim();

const fileUrlOrNull = (url) => {
  if (url == null) return null;
  const s = String(url).trim();
  return s ? s : null;
};

const fileLabelFromUrl = (url) => {
  if (url == null || String(url).trim() === "") return "—";
  try {
    const last = new URL(String(url)).pathname.split("/").filter(Boolean).pop();
    return last
      ? decodeURIComponent(last.replace(/\+/g, " "))
      : String(url);
  } catch {
    return String(url);
  }
};

/** Full phone number for comparison/display: country code + national number (like the current display in the file). */
const fullPhoneFromParts = (dial, national) => {
  const d =
    dial != null && String(dial).trim() !== ""
      ? normalizeDialCode(dial)
      : "";
  const n = String(national ?? "").replace(/\s+/g, "");
  const s = `${d}${n}`;
  return s || "—";
};

const cityLabel = (c, lang) => {
  if (!c) return "—";
  return lang === "ar"
    ? c.name_ar || c.name_en || "—"
    : c.name_en || c.name_ar || "—";
};

const currentPhone = (owner) => {
  if (!owner) return "—";
  return fullPhoneFromParts(owner.country?.dial_code, owner.phone_number);
};

/**
 * @param {object} attrs Normalized edit request (attributes + nested `company`)
 * @param {string} lang
 * @param {object} reg registrationReq messages
 * @param {object} cl companiesList messages
 */
export function buildEditRequestDiff(attrs, lang, reg, cl) {
  const company = attrs?.company || {};
  const owner = company.company_owner || {};
  const companyFields = [];
  const contactFields = [];
  let changeCount = 0;

  const push = (arr, label, proposedVal, oldDisplay, newDisplayFn) => {
    const has =
      proposedVal !== null &&
      proposedVal !== undefined &&
      !(typeof proposedVal === "string" && proposedVal === "");
    if (!has) {
      arr.push({
        label,
        changed: false,
        display: oldDisplay,
      });
      return;
    }
    const newDisplay = newDisplayFn(proposedVal);
    const changed = norm(oldDisplay) !== norm(newDisplay);
    if (changed) changeCount += 1;
    arr.push({
      label,
      changed,
      oldValue: oldDisplay,
      newValue: newDisplay,
      display: changed ? null : oldDisplay,
    });
  };

  push(
    companyFields,
    reg.englishName,
    attrs.company_name_en,
    company.name_en || company.name || "—",
    (v) => v || "—",
  );
  push(
    companyFields,
    reg.arabicName,
    attrs.company_name_ar,
    company.name_ar || "—",
    (v) => v || "—",
  );
  push(
    companyFields,
    reg.city,
    attrs.company_city,
    cityLabel(company.city, lang),
    (v) => cityLabel(v, lang),
  );

  const crOld =
    company.commercial_record_url || company.commercial_record
      ? company.commercial_record || "—"
      : "—";
  companyFields.push({
    label: reg.commercialRecord,
    changed: false,
    display: crOld,
    valueAccent: crOld !== "—",
  });

  push(
    contactFields,
    reg.fullName,
    attrs.company_owner_name,
    owner.name || "—",
    (v) => v || "—",
  );
  // The phone: The API sends `company_owner_phone_number` without the country code and the proposed country as `company_owner_country`;
  // We need to merge them before comparing with `currentPhone` or else it will show a fake change (+20… vs. just the local number).
  const proposedNational = attrs.company_owner_phone_number;
  const hasPhoneProposal =
    proposedNational !== null &&
    proposedNational !== undefined &&
    !(typeof proposedNational === "string" && proposedNational === "");

  const mobileFieldExtras = { valueClassName: "phone-number" };

  if (!hasPhoneProposal) {
    contactFields.push({
      label: reg.mobileNumber,
      changed: false,
      display: currentPhone(owner),
      ...mobileFieldExtras,
    });
  } else {
    const dialForProposed =
      attrs.company_owner_country?.dial_code || owner.country?.dial_code;
    const newPhoneDisplay = fullPhoneFromParts(dialForProposed, proposedNational);
    const oldPhoneDisplay = currentPhone(owner);
    const changed = norm(oldPhoneDisplay) !== norm(newPhoneDisplay);
    if (changed) changeCount += 1;
    contactFields.push(
      changed
        ? {
            label: reg.mobileNumber,
            changed: true,
            oldValue: oldPhoneDisplay,
            newValue: newPhoneDisplay,
            ...mobileFieldExtras,
          }
        : {
            label: reg.mobileNumber,
            changed: false,
            display: oldPhoneDisplay,
            ...mobileFieldExtras,
          },
    );
  }

  const emailDisplay = owner.email && String(owner.email).trim() ? owner.email : "—";
  contactFields.push({
    label: cl.emailAddress,
    changed: false,
    display: emailDisplay,
    valueAccent: emailDisplay !== "—",
  });

  const documentFields = [];
  const pushDoc = (label, oldUrl, proposedVal) => {
    const oldDisp = fileLabelFromUrl(oldUrl);
    const oldFileUrl = fileUrlOrNull(oldUrl);
    // The API often sets `null` for fields that were not touched in the request; if treated as a proposed value
    // it shows a phantom change (old file → "—") and increments the counter. We consider null/undefined = no draft for this attachment.
    if (proposedVal === undefined || proposedVal === null) {
      documentFields.push({
        label,
        changed: false,
        display: oldDisp,
        fileUrl: oldFileUrl,
      });
      return;
    }
    const newDisp = fileLabelFromUrl(proposedVal);
    const changed = norm(oldUrl || "") !== norm(String(proposedVal ?? ""));
    const newFileUrl = fileUrlOrNull(proposedVal);
    if (changed) changeCount += 1;
    documentFields.push(
      changed
        ? {
            label,
            changed: true,
            oldValue: oldDisp,
            newValue: newDisp,
            oldFileUrl,
            newFileUrl,
          }
        : {
            label,
            changed: false,
            display: oldDisp,
            fileUrl: oldFileUrl,
          },
    );
  };

  pushDoc(
    reg.delegationDocument,
    owner.delegation_document_url,
    attrs.company_owner_delegation_document_url,
  );
  pushDoc(
    reg.idDocument,
    owner.personal_id_url,
    attrs.company_owner_personal_id_url,
  );
  pushDoc(
    reg.commercialRecordDocument,
    company.commercial_record_url,
    attrs.company_commercial_record_url,
  );

  return { companyFields, contactFields, documentFields, changeCount };
}
